import { useNavigate, useOutletContext } from "react-router-dom";
import type { MeetingRunContext } from "../MeetingRunContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import {
  createVersionOfAgendaItemFromExisting,
  findModuleByItemId,
  getVoters,
} from "../utilities";
import { Button } from "@repo/ui/components/Base/Button";
import { cn } from "@repo/shared/utils";
import { CollapsiblePanel } from "../CollapsiblePanel";
import { getSectionById } from "./sections";
import type { MeetingRunSectionProps } from "./meetingRunSectionProps";
import { AppUrlPath } from "@/lib/AppUrlPath";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";

export const MeetingRunMotionSection = ({
  id,
  idSuffix,
  className,
  open,
  onToggle,
}: MeetingRunSectionProps) => {
  const {
    tenantId,
    meeting: _meeting,
    team,
    userMap,
    agendaItem: _item,
    mutationSystem,
    noTimestamps,
  } = useOutletContext() as MeetingRunContext;

  const navigate = useNavigate();

  const mutationKey = "agendaItem:" + _item!.id;

  const agendaItemMutation: Mutator<AgendaItemDto> = useFutureCallback(
    async (id, data) => {
      await agendaItemRepo.update(tenantId, id, data);
    }
  );
  const agendaItem = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _item!
  );
  const meeting = useImmediateMutationSystem(
    mutationSystem,
    "meeting:" + _meeting.id,
    _meeting
  );

  const votingUsers = getVoters({ meeting, team, userMap });

  return (
    <CollapsiblePanel
      id={id}
      idSuffix={idSuffix}
      title={getSectionById("move").title}
      className={className}
      open={open}
      onToggle={onToggle}
    >
      <h1 className="mt-5 text-2xl font-bold">Motion moved by</h1>
      <p>Pick the member that moved the motion:</p>

      <div className="flex flex-row flex-wrap gap-2 mt-2">
        {votingUsers.all.map((user) => (
          <Button
            key={user.id}
            tooltip={
              votingUsers.absent.includes(user) ? "User is absent" : undefined
            }
            disabled={
              agendaItem.review?.seconded === user.id ||
              votingUsers.absent.includes(user)
            }
            className={cn(
              "flex-col h-auto font-bold border-[2px] border-primary",
              agendaItem.review?.moved === user.id
                ? "bg-primary hover:bg-primary/90"
                : "text-primary bg-transparent hover:bg-transparent",
              agendaItem.review?.seconded === user.id && "opacity-60"
            )}
            onClick={() => {
              const moved = agendaItem.review?.moved === user.id ? "" : user.id;

              const timestamp = noTimestamps ? {} : { timestamp: Date.now() };

              mutationSystem.debounce(
                mutationKey,
                agendaItemMutation,
                {
                  review: {
                    ...agendaItem.review,
                    meetingId: meeting.id,
                    moved,
                    originalMotion: agendaItem.motion,
                    ...timestamp,
                  },
                },
                agendaItem.id
              );
            }}
          >
            {user.firstName}
            <br />
            {user.lastName}
          </Button>
        ))}
      </div>

      <h1 className="mt-5 text-2xl font-bold">Motion seconded by</h1>
      <p>Pick the member that seconded the motion:</p>

      <div className="flex flex-row flex-wrap gap-2 mt-2">
        {votingUsers.all.map((user) => (
          <Button
            key={user.id}
            tooltip={
              votingUsers.absent.includes(user) ? "User is absent" : undefined
            }
            disabled={
              agendaItem.review?.moved === user.id ||
              votingUsers.absent.includes(user)
            }
            className={cn(
              "flex-col h-auto font-bold border-[2px] border-secondary",
              agendaItem.review?.seconded === user.id
                ? "bg-secondary hover:bg-secondary/90"
                : "text-secondary bg-transparent hover:bg-transparent",
              agendaItem.review?.moved === user.id && "opacity-60"
            )}
            onClick={() => {
              const seconded =
                agendaItem.review?.seconded === user.id ? "" : user.id;

              mutationSystem.debounce(
                mutationKey,
                agendaItemMutation,
                {
                  review: {
                    ...agendaItem.review,
                    meetingId: meeting.id,
                    seconded,
                  },
                },
                agendaItem.id
              );
            }}
          >
            {user.firstName}
            <br />
            {user.lastName}
          </Button>
        ))}
        <Button
          key="none"
          className={cn(
            "flex-col h-auto font-bold border-[2px] border-secondary",
            agendaItem.review?.seconded === "none"
              ? "bg-secondary hover:bg-secondary/90"
              : "text-secondary bg-transparent hover:bg-transparent"
          )}
          onClick={() => {
            const seconded =
              agendaItem.review?.seconded === "none" ? "" : "none";

            mutationSystem.debounce(
              mutationKey,
              agendaItemMutation,
              {
                review: {
                  ...agendaItem.review,
                  meetingId: meeting.id,
                  seconded,
                  status: "rejected",
                },
              },
              agendaItem.id
            );
          }}
        >
          Nobody
        </Button>
      </div>
      {agendaItem.review?.seconded === "none" && (
        <div>
          <Button
            className="mt-4 border-[2px] border-destructive text-destructive bg-transparent hover:bg-transparent"
            disabled={agendaItem.newVersionOfTheItemId !== undefined}
            onClick={async () => {
              if (!meeting.agenda?.agendaModules) return;

              const agendaModule = findModuleByItemId(
                meeting.agenda?.agendaModules || [],
                agendaItem.id
              );
              if (!agendaModule) return;

              const newItem = await createVersionOfAgendaItemFromExisting({
                existingItem: agendaItem,
                tenantId,
                agendaModule: agendaModule,
                meeting,
                mutationSystem,
              });
              mutationSystem.debounce(
                mutationKey,
                agendaItemMutation,
                { newVersionOfTheItemId: newItem.id },
                agendaItem.id
              );
              const newPath = new AppUrlPath(tenantId).getMeetingRunPath(
                meeting.id,
                newItem.id
              );
              navigate(newPath);
            }}
          >
            New Agenda Item
          </Button>
        </div>
      )}
    </CollapsiblePanel>
  );
};
